import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Image as ImageIcon, Loader2 } from 'lucide-react'
import { contentAssetThumbnailUrl } from '../features/content-funnels/managedApi'
import { AuthorizedMediaImage } from '../shared/media/authorizedMedia'
import {
  funnelPlanRowKey,
} from './contentFunnelSelection.logic'
import type {
  FunnelPlan,
  FunnelPlanRow,
  FunnelSelectionCandidate,
} from './contentFunnelSelection.types'

interface ContentFunnelSelectionStepProps {
  plan: FunnelPlan
  choices: Record<string, string>
  onChoiceChange: (rowKey: string, value: string) => void
  templatePreviews?: Record<string, string>
}

function choiceStatus(row: FunnelPlanRow, value: string) {
  if (!value) return { label: 'Не выбрано', className: 'bg-gray-100 text-gray-700 dark:bg-zinc-800 dark:text-zinc-300' }
  const [versionId, ruleId, source] = value.split(':')
  const automatic = row.automatic_selection
  if (
    source === 'auto' &&
    automatic?.version_id === Number(versionId) &&
    automatic?.rule_id === Number(ruleId)
  ) {
    return { label: 'Автоматически', className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200' }
  }
  return { label: 'Вручную', className: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-200' }
}

function candidateValue(candidate: FunnelSelectionCandidate, row: FunnelPlanRow) {
  const automatic = row.automatic_selection
  const source =
    automatic?.version_id === candidate.version_id &&
    automatic?.rule_id === candidate.rule_id
      ? 'auto'
      : 'manual'
  return `${candidate.version_id}:${candidate.rule_id}:${source}`
}

export default function ContentFunnelSelectionStep({
  plan,
  choices,
  onChoiceChange,
  templatePreviews = {},
}: ContentFunnelSelectionStepProps) {
  const templates = useMemo(() => {
    const groups = new Map<string, FunnelPlanRow[]>()
    for (const row of plan.rows) {
      groups.set(row.template_key, [...(groups.get(row.template_key) || []), row])
    }
    return [...groups.entries()]
  }, [plan.rows])
  const totalCount = plan.rows.reduce(
    (sum, row) => sum + row.product_articles.length,
    0,
  )
  const selectedCount = plan.rows.reduce(
    (sum, row) => (
      sum + (choices[funnelPlanRowKey(row)] ? row.product_articles.length : 0)
    ),
    0,
  )

  if (!plan.rows.length) {
    return (
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
        Матрица товаров не вернула строк для выбранных макетов. Создание не запущено.
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
          Контент-воронки для карточек
        </h4>
        <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
          Товары и файлы печати будут созданы для всех строк. Пустой выбор отключает
          карточку и медиаконтент только для конкретного товара и кабинета.
        </p>
        <p className="mt-2 text-xs font-medium text-gray-700 dark:text-zinc-300">
          Карточки: {selectedCount} из {totalCount} · пропущено: {totalCount - selectedCount}
        </p>
      </div>

      {templates.map(([templateKey, templateRows]) => (
        <section
          key={templateKey}
          className="rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:border-zinc-700 dark:bg-zinc-900/40"
        >
          <div className="mb-3 flex items-center gap-3">
            <div className="flex h-16 w-12 shrink-0 items-center justify-center overflow-hidden rounded-md bg-gray-100 dark:bg-zinc-800">
              {templatePreviews[templateKey] ? (
                <img src={templatePreviews[templateKey]} alt="" className="h-full w-full object-contain" />
              ) : (
                <ImageIcon className="h-6 w-6 text-gray-400" />
              )}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">{templateRows[0].template_name}</p>
              <p className="text-xs text-gray-500">{templateRows[0].design_type}</p>
              <p className="text-2xs text-gray-400">Код макета будет присвоен после создания</p>
            </div>
          </div>

          <div className="space-y-3">
            {[...new Map(
              templateRows.map((row) => [
                `${row.marketplace}:${row.seller_id}:${row.cabinet}`,
                templateRows.filter((candidate) =>
                  candidate.marketplace === row.marketplace &&
                  candidate.seller_id === row.seller_id &&
                  candidate.cabinet === row.cabinet
                ),
              ]),
            ).entries()].map(([cabinetKey, rows]) => (
              <div key={cabinetKey} className="rounded-lg border border-gray-200 p-2 dark:border-zinc-700">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className={`rounded px-2 py-1 text-2xs font-bold uppercase text-white ${rows[0].marketplace === 'wb' ? 'bg-fuchsia-600' : 'bg-blue-600'}`}>
                    {rows[0].marketplace}
                  </span>
                  <span className="text-sm font-semibold">{rows[0].cabinet}</span>
                  <span className="text-xs text-gray-500">seller #{rows[0].seller_id}</span>
                </div>
                <div className="space-y-2">
            {rows.map((row) => {
              const key = funnelPlanRowKey(row)
              const value = choices[key] || ''
              const status = choiceStatus(row, value)
              const selected = row.candidates.find(
                (candidate) => candidateValue(candidate, row) === value,
              )
              const thumbnailAssetId = selected?.thumbnail_asset_ids[0]
              return (
                <article
                  key={key}
                  className="grid gap-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900 sm:grid-cols-[60px_minmax(0,1fr)]"
                >
                  <div className="flex h-20 w-[60px] items-center justify-center overflow-hidden rounded-md bg-gray-100 dark:bg-zinc-800">
                    {thumbnailAssetId ? (
                      <AuthorizedMediaImage
                        src={contentAssetThumbnailUrl(thumbnailAssetId)}
                        thumbnailWidth={160}
                        alt=""
                        className="h-full w-full object-contain"
                        loadingFallback={<Loader2 className="h-5 w-5 animate-spin text-gray-400" />}
                        errorFallback={<ImageIcon className="h-6 w-6 text-gray-400" />}
                      />
                    ) : (
                      <ImageIcon className="h-6 w-6 text-gray-400" />
                    )}
                  </div>
                  <div className="min-w-0 space-y-2">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="text-xs text-gray-500">
                          {row.category_prefix}
                          {row.category_name ? ` · ${row.category_name}` : ''}
                        </p>
                      </div>
                      <span className={`rounded-full px-2 py-1 text-2xs font-semibold ${status.className}`}>
                        {status.label}
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-1">
                      {row.product_articles.map((product) => (
                        <span
                          key={product.quantity_index}
                          className="rounded bg-gray-100 px-2 py-1 text-2xs dark:bg-zinc-800"
                        >
                          {row.category_prefix}{product.suffix
                            ? ` · товар ${product.suffix}`
                            : ' · один товар'}
                        </span>
                      ))}
                      <span className="rounded border border-gray-200 px-2 py-1 text-2xs dark:border-zinc-700">
                        {row.required_generation_mode === 'multi' ? 'multi' : 'single'}
                      </span>
                    </div>

                    {!row.automatic_selection && (
                      <p className="flex items-center gap-1 text-2xs text-amber-700 dark:text-amber-300">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        {row.automatic_status === 'ambiguous'
                          ? 'Автовыбор неоднозначен — выберите вручную или пропустите.'
                          : 'Автовыбор не найден — выберите совместимую воронку или пропустите.'}
                      </p>
                    )}

                    <select
                      value={value}
                      onChange={(event) => onChoiceChange(key, event.target.value)}
                      aria-label={`Контент-воронка ${row.template_name} ${row.category_prefix} ${row.cabinet}`}
                      className="min-h-11 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                    >
                      <option value="">Не создавать карточки</option>
                      {row.candidates.map((candidate) => (
                        <option
                          key={`${candidate.version_id}:${candidate.rule_id}`}
                          value={candidateValue(candidate, row)}
                        >
                          {candidate.name} · v{candidate.version_no} · {candidate.slide_count} слайд.
                        </option>
                      ))}
                    </select>
                  </div>
                </article>
              )
            })}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}
